import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  linkWithCredential,
  signInWithCustomToken,
  updatePhoneNumber,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Linking, Platform } from 'react-native';

import { auth } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';

import { normalizeUsPhoneNumber } from './phone-number';
import type { VerifiedAccount } from './verification-status';

type VerifiedAccountResponse = VerifiedAccount;

type IdentitySessionResponse = {
  url: string | null;
  verification: VerifiedAccountResponse;
};
type IdentitySessionRequest = {
  returnOrigin?: string;
};

type PhoneHandoffResponse = {
  expiresAtMillis: number;
  url: string;
};

type CheckoutSessionResponse = {
  alreadyPaid: boolean;
  clientSecret: string | null;
  url: string | null;
  verification: VerifiedAccountResponse;
};
type CheckoutSessionRequest = {
  returnOrigin?: string;
  uiMode?: 'embedded' | 'hosted';
};

const syncVerifiedAccountStatusCallable = httpsCallable<
  Record<string, never>,
  VerifiedAccountResponse
>(functions, 'syncVerifiedAccountStatus');
const syncCurrentAuthPhoneCallable = httpsCallable<
  Record<string, never>,
  VerifiedAccountResponse
>(functions, 'syncCurrentAuthPhone');
const createIdentityVerificationSessionCallable = httpsCallable<
  IdentitySessionRequest,
  IdentitySessionResponse
>(functions, 'createIdentityVerificationSession');
const createVerifiedAccountCheckoutSessionCallable = httpsCallable<
  CheckoutSessionRequest,
  CheckoutSessionResponse
>(functions, 'createVerifiedAccountCheckoutSession');
const requestVerifiedAccountRefundCallable = httpsCallable<
  Record<string, never>,
  VerifiedAccountResponse
>(functions, 'requestVerifiedAccountRefund');
const createPhoneVerificationHandoffCallable = httpsCallable<
  Record<string, never>,
  PhoneHandoffResponse
>(functions, 'createPhoneVerificationHandoff');
let phoneRecaptchaVerifier: RecaptchaVerifier | null = null;
let phoneRecaptchaWidgetId: number | null = null;

function currentUserOrThrow() {
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('You must be signed in to verify your account.');
  }

  return currentUser;
}

function getOrCreateRecaptchaContainer() {
  if (typeof document === 'undefined') {
    throw new Error('Phone verification needs a browser window.');
  }

  const containerId = 'frienzzi-phone-verification-recaptcha';
  let container = document.getElementById(containerId);

  if (!container) {
    container = document.createElement('div');
    container.id = containerId;
    container.style.minHeight = '78px';
    document.body.appendChild(container);
  }

  return containerId;
}

async function getPhoneRecaptchaVerifier() {
  if (phoneRecaptchaVerifier) {
    return phoneRecaptchaVerifier;
  }

  phoneRecaptchaVerifier = new RecaptchaVerifier(
    auth,
    getOrCreateRecaptchaContainer(),
    {
      size: 'normal',
    },
  );
  phoneRecaptchaWidgetId = await phoneRecaptchaVerifier.render();

  return phoneRecaptchaVerifier;
}

function resetPhoneRecaptchaVerifier() {
  const grecaptchaHost = globalThis as typeof globalThis & {
    grecaptcha?: { reset?: (widgetId?: number) => void };
  };

  if (phoneRecaptchaWidgetId !== null) {
    grecaptchaHost.grecaptcha?.reset?.(phoneRecaptchaWidgetId);
  }
}

function clearPhoneRecaptchaVerifier() {
  phoneRecaptchaVerifier?.clear();
  phoneRecaptchaVerifier = null;
  phoneRecaptchaWidgetId = null;
}

async function openVerificationUrl(url: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.assign(url);
    return;
  }

  await Linking.openURL(url);
}

function phoneVerificationError(error: unknown) {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: unknown }).code)
      : '';

  if (code === 'auth/invalid-app-credential') {
    return new Error(
      'Firebase could not validate this browser for SMS verification. Refresh the page and try again.',
    );
  }

  return error;
}

export async function refreshVerifiedAccountStatus() {
  const result = await syncVerifiedAccountStatusCallable({});

  return result.data;
}

export async function sendPhoneVerificationCode(phoneNumber: string) {
  const normalizedPhoneNumber = normalizeUsPhoneNumber(phoneNumber);

  if (!normalizedPhoneNumber) {
    throw new Error('Enter a valid US cell phone number.');
  }

  if (Platform.OS !== 'web') {
    const result = await createPhoneVerificationHandoffCallable({});

    await Linking.openURL(result.data.url);
    return { handoff: true as const };
  }

  currentUserOrThrow();
  const verifier = await getPhoneRecaptchaVerifier();

  try {
    const provider = new PhoneAuthProvider(auth);
    const verificationId = await provider.verifyPhoneNumber(
      normalizedPhoneNumber,
      verifier,
    );

    return { handoff: false as const, verificationId };
  } catch (error) {
    resetPhoneRecaptchaVerifier();
    clearPhoneRecaptchaVerifier();
    throw phoneVerificationError(error);
  }
}

export async function confirmPhoneVerificationCode({
  code,
  verificationId,
}: {
  code: string;
  verificationId: string;
}) {
  const currentUser = currentUserOrThrow();
  const credential = PhoneAuthProvider.credential(
    verificationId,
    code.trim(),
  );
  const hasPhoneProvider = currentUser.providerData.some(
    (provider) => provider.providerId === 'phone',
  );

  if (hasPhoneProvider) {
    await updatePhoneNumber(currentUser, credential);
  } else {
    await linkWithCredential(currentUser, credential);
  }

  await currentUser.getIdToken(true);
  await currentUser.reload();

  const result = await syncCurrentAuthPhoneCallable({});

  return result.data;
}

export async function startIdentityVerification() {
  const result = await createIdentityVerificationSessionCallable({
    returnOrigin:
      typeof window !== 'undefined' ? window.location.origin : undefined,
  });
  const url = result.data.url;

  if (!url) {
    return {
      opened: false,
      verification: result.data.verification,
    };
  }

  await openVerificationUrl(url);

  return {
    opened: true,
    verification: result.data.verification,
  };
}

export async function startVerifiedAccountCheckout() {
  if (Platform.OS !== 'web') {
    await Linking.openURL('https://frienzzi.com/verify-account');
    return null;
  }

  const result = await createVerifiedAccountCheckoutSessionCallable({
    returnOrigin:
      typeof window !== 'undefined' ? window.location.origin : undefined,
    uiMode: 'hosted',
  });
  const url = result.data.url;

  if (!url) {
    return result.data.verification;
  }

  await Linking.openURL(url);

  return result.data.verification;
}

export async function createEmbeddedVerifiedAccountCheckoutSession() {
  const result = await createVerifiedAccountCheckoutSessionCallable({
    returnOrigin:
      typeof window !== 'undefined' ? window.location.origin : undefined,
    uiMode: 'embedded',
  });

  return result.data;
}

export async function requestVerifiedAccountRefund() {
  const result = await requestVerifiedAccountRefundCallable({});

  return result.data;
}

export async function redeemPhoneVerificationHandoff(token: string) {
  const response = await fetch(
    'https://us-central1-frienzzi.cloudfunctions.net/redeemPhoneVerificationHandoff',
    {
      body: JSON.stringify({ token }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    },
  );

  if (!response.ok) {
    throw new Error('That phone verification link expired. Start again.');
  }

  const data = (await response.json()) as { customToken?: string };

  if (!data.customToken) {
    throw new Error('That phone verification link is invalid.');
  }

  await signInWithCustomToken(auth, data.customToken);
  await auth.currentUser?.getIdToken(true);

  return auth.currentUser;
}
